import React from 'react';
import { StudioFilterPill } from 'src/components/star-grid/components/StudioFilterPill';
import { isStudioBindingSelectable, studioDimensionBindings } from 'src/components/star-grid/utils/studioBindings';
import Button from '@amzn/awsui-components-core/button';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import Modal from '@amzn/awsui-components-core/modal';
import Spinner from '@amzn/awsui-components-core/spinner';
import Alert from '@amzn/awsui-components-core/alert';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StudioField, StudioDataset, StudioLineSet } from 'src/components/star-grid/reportStudio.types';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';
import { useStudioField } from 'src/components/star-grid/hooks/useStudioField';
import { StudioSelect } from 'src/components/star-grid/components/StudioControls';
import MemberSubsetModal from 'src/components/star-grid/components/subset-editor/MemberSubsetModal';
import { ReportParameterFilter } from 'src/components/star-grid/components/ReportParameterFilter';
import { useReportParameter } from 'src/components/star-grid/hooks/useReportParameter';
import type { ReportParameter } from 'src/components/star-grid/reportDefinition.types';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
interface Props {
  readonly compact?: boolean;
  readonly singleMember?: boolean;
  readonly shelf: 'rows' | 'filters';
  readonly field: StudioField;
  readonly dataset: StudioDataset;
  readonly lineSets: readonly StudioLineSet[];
  readonly text: StarGridText;
  readonly onChange: (field: StudioField) => void;
  readonly onRemove: () => void;
  readonly onMove: (offset: number) => void;
  //: For a row backed by a line set (the P&L statement), Edit jumps to the Line Set editor instead
  //: of the generic field modal, so it goes straight to the line records.
  readonly onEditLineSet?: () => void;
  //: Set on a row just added from the palette: the member modal opens at once, and what is picked
  //: becomes the row's line set. Cancelling removes the row.
  readonly picking?: boolean;
  readonly onPicked?: (members: readonly string[], tree?: PnlHierarchyTree) => void;
  readonly onCancelPick?: () => void;
}
export const StudioFieldEditor: React.FC<Props> = ({
  compact,
  singleMember,
  shelf,
  field,
  dataset,
  lineSets,
  text,
  onChange,
  onRemove,
  onMove,
  onEditLineSet,
  picking,
  onPicked,
  onCancelPick
}) => {
  const Container = !compact && shelf === 'filters' ? StudioFilterPill : 'article';
  const binding = dataset.bindings.find((item) => item.id === field.binding_id)!;
  const state = useStudioField(field, binding, onChange, singleMember, picking);
  // The member modal dismisses itself after OK too, so only a dismiss BEFORE a pick cancels the add.
  const picked = React.useRef(false);
  const finishPick = (members: readonly string[]) => {
    picked.current = true;
    state.setDetailsOpen(false);
    onPicked?.(members, state.choices.tree);
  };
  const dismiss = () => (picking && onCancelPick && !picked.current ? onCancelPick() : state.setDetailsOpen(false));
  // The set this field already points at is offered too: the deck's rows SHARE one set, so a second row
  // dimension's set is owned by the first and filtering on the binding alone left the chip unlabelled.
  const sets = lineSets.filter((set) => set.binding_id === binding.id || set.id === field.selection.reference_id);
  // A binding IS a hierarchy id, so the preview filter reuses the report parameter machinery rather
  // than a second member picker with its own behaviour.
  const previewFilterParameter: ReportParameter = React.useMemo(
    () => ({
      key: field.id,
      label: binding.label,
      role: 'fact_filter' as const,
      required: false,
      multiple: !singleMember,
      hierarchy_id: binding.id,
      options_available: true,
      default: [] as string[]
    }),
    [field.id, binding.id, binding.label, singleMember]
  );
  const previewFilterState = useReportParameter(
    previewFilterParameter,
    field.selection.kind === 'members' ? [...field.selection.members] : [],
    compact === true
  );
  /*
   * The hierarchy choice belongs to the HOST (it drives `field.binding_id` and the tree load), but it
   * belongs on the editor's toolbar row VISUALLY. So it is declared once and rendered through the editor's
   * `toolbarLeading` slot while the editor is up, and on its own only when it is not — otherwise a failed
   * or unavailable hierarchy would leave no way to pick a different one.
   */
  const membersEditorOpen =
    state.detailsOpen &&
    state.usesMembers &&
    !state.choices.loading &&
    Boolean(state.choices.tree) &&
    state.choices.tree!.hierarchy_id === (binding.tree_id ?? binding.id);
  const hierarchySelect = (
    <StudioSelect
      inline
      label={text('studioHierarchy')}
      testId={`studio-binding-${field.id}`}
      value={field.binding_id}
      selectedLabel={binding.hierarchy}
      options={studioDimensionBindings(dataset, binding.id).map((item) => ({ id: item.id, label: item.hierarchy }))}
      disabled={singleMember}
      onChange={(binding_id) => onChange({ ...field, binding_id, selection: { kind: 'all', members: [] } })}
    />
  );

  return (
    <Container
      className={compact || shelf === 'filters' ? undefined : styles.rowItem}
      data-testid={compact ? 'studio-preview-field' : 'studio-field'}
    >
      {compact ? (
        // A dropdown, not a dialog: the preview bar filters the grid directly beneath it, and a modal
        // covered the very rows whose change you were trying to judge. Same control the saved report's
        // filter bar uses, so filtering behaves identically before and after saving.
        <ReportParameterFilter
          parameter={previewFilterParameter}
          values={field.selection.kind === 'members' ? [...field.selection.members] : []}
          state={previewFilterState}
          onChange={(_key, selected) => onChange({ ...field, selection: { kind: 'members', members: [...selected] } })}
        />
      ) : (
        <div className={styles.cardActions}>
          <span className={styles.grip} aria-hidden>
            ⠿
          </span>
          <strong>{binding.label}</strong>
          <span className={shelf === 'filters' ? styles.filterSummary : styles.fieldSummary}>
            {shelf === 'filters' && field.selection.kind === 'members'
              ? state.choices.selected.map((item) => item.label).join(', ')
              : field.selection.kind === 'line_set'
              ? sets.find((set) => set.id === field.selection.reference_id)?.label
              : text(`studioSelection_${field.selection.kind}`)}
          </span>
          <Button
            variant="icon"
            iconName="edit"
            ariaLabel={text('studioEdit')}
            onClick={() => (onEditLineSet ? onEditLineSet() : state.setDetailsOpen(true))}
          />
          <span className={styles.spacer} />
          {shelf === 'rows' && !singleMember && (
            <>
              <Button variant="icon" iconName="angle-up" ariaLabel={text('studioMoveUp')} onClick={() => onMove(-1)} />
              <Button variant="icon" iconName="angle-down" ariaLabel={text('studioMoveDown')} onClick={() => onMove(1)} />
            </>
          )}
          <Button variant="icon" iconName="close" ariaLabel={text('studioRemoveField')} onClick={onRemove} />
        </div>
      )}
      <Modal
        visible={state.detailsOpen}
        // Hosts the member editor embedded, so THIS size — not the editor's own — is what its tree and
        // search box actually get. `x-large` matches the size the editor used as a standalone dialog.
        size="x-large"
        data-testid={`studio-dimension-${field.id}`}
        onDismiss={dismiss}
        // Titled as the editor titled ITSELF when it rendered its own dialog: embedded mode drops the
        // Modal `header` prop, so the host has to carry that text or the dialog loses it.
        header={`Select Elements - ${binding.label}`}
        closeAriaLabel={text('studioClose')}
        // `undefined`, not `false`, when the editor supplies its own Cancel/OK: a defined-but-falsy
        // footer still reserves the Modal's footer band, which showed up as dead space under the buttons.
        footer={
          !state.usesMembers || (!state.choices.loading && !state.choices.tree) ? (
            <Button onClick={() => (picking && onPicked ? finishPick([]) : state.setDetailsOpen(false))}>{text('studioDone')}</Button>
          ) : undefined
        }
      >
        <SpaceBetween size="xs">
          {!membersEditorOpen && hierarchySelect}
          {!isStudioBindingSelectable(dataset, binding.id) && <Alert type="warning">{text('studioHierarchyUnavailable')}</Alert>}
          {field.selection.kind === 'subset' && (
            <StudioSelect
              label={text('studioSubset')}
              value={field.selection.reference_id ?? ''}
              options={binding.subsets}
              onChange={(reference_id) => onChange({ ...field, selection: { ...field.selection, reference_id } })}
            />
          )}
          {field.selection.kind === 'line_set' && (
            <StudioSelect
              label={text('studioLineSet')}
              value={field.selection.reference_id ?? ''}
              options={sets}
              onChange={(reference_id) => onChange({ ...field, selection: { ...field.selection, reference_id } })}
            />
          )}
          {['children', 'descendants'].includes(field.selection.kind) && (
            <Checkbox
              checked={Boolean(field.selection.include_root)}
              onChange={({ detail }) => onChange({ ...field, selection: { ...field.selection, include_root: detail.checked } })}
            >
              {text('studioIncludeRoot')}
            </Checkbox>
          )}
          {state.choices.error && (
            <Alert type="error" action={<Button onClick={state.choices.reload}>{text('retry')}</Button>}>
              {state.choices.error.message === 'studioInvalidHierarchyWeights' ? text('studioInvalidHierarchyWeights') : state.choices.error.message}
            </Alert>
          )}
          {state.usesMembers && state.choices.loading && <Spinner />}
          {state.usesMembers && !state.choices.loading && !state.choices.tree && !state.choices.error && binding.rollup && (
            <Alert type="info">{text('studioHierarchyMembersUnavailable')}</Alert>
          )}
          {membersEditorOpen && (
            <MemberSubsetModal
              key={`${field.binding_id}:${field.selection.kind}`}
              embedded
              visible
              hierarchyId={state.choices.tree!.hierarchy_id}
              hierarchyTree={state.choices.tree!}
              toolbarLeading={hierarchySelect}
              dimensionLabel={binding.label}
              multiple={state.multiple}
              requireSelection={!state.multiple}
              initialChecked={state.initialChecked}
              onConfirm={picking && onPicked ? finishPick : state.confirm}
              pickOrder={picking}
              onDismiss={dismiss}
              testId={`studio-members-${field.id}`}
            />
          )}
        </SpaceBetween>
      </Modal>
    </Container>
  );
};
